import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Car, Loader2, Search, UserX, KeyRound, Wrench, AlertTriangle, FileBadge, CheckCircle2, LayoutGrid, Rows3, ClipboardList } from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../lib/auth';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { VehicleAssignDialog } from '../../components/VehicleAssignDialog';
import { VehicleOverviewTable } from './VehicleOverviewTable';
import { VehicleCard } from './VehicleCard';
import { VehicleAlerts } from './VehicleAlerts';
import { VergunningDialog } from './VergunningDialog';
import { RowEditor } from './RowEditor';
import { FILTERS, matchesFilter, normalizeTr } from './utils';

const VIEW_KEY = 'vehicle-overview-view';

function Kpi({ icon: Icon, label, value, tone, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left rounded-xl px-3 py-2.5 transition-[background-color,box-shadow] duration-150 ${active ? 'bg-white text-slate-900 shadow-lg' : 'bg-white/5 text-white hover:bg-white/10'}`}>
      <div className={`flex items-center justify-between text-[11px] ${active ? 'text-slate-500' : 'text-slate-400'}`}><span>{label}</span><Icon className={`h-4 w-4 ${tone}`} /></div>
      <div className="text-2xl font-black mt-0.5 tabular-nums">{value}</div>
    </button>
  );
}

export default function VehicleOverview() {
  const { user } = useAuthStore();
  const isSuper = user?.role === 'SUPER_ADMIN';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [firmId, setFirmId] = useState('');
  const [firms, setFirms] = useState([]);
  const [brandIcons, setBrandIcons] = useState(new Set());
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'cards');
  const [expanded, setExpanded] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [vergFor, setVergFor] = useState(null);

  useEffect(() => { if (isSuper) api.get('/firms/').then((r) => setFirms(r.data?.data || [])).catch(() => {}); }, [isSuper]);
  useEffect(() => {
    api.get('/brand-icons').then((r) => setBrandIcons(new Set((r.data?.data || []).map((i) => (i.brand || '').toUpperCase())))).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/vehicles/overview', { params: firmId ? { firm_id: firmId } : {} });
      setData(res.data?.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Liste yüklenemedi'); }
    finally { setLoading(false); }
  }, [firmId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = normalizeTr(search.trim());
    return (data?.rows || []).filter((r) => matchesFilter(r, filter) && (!q
      || normalizeTr(r.license_plate).includes(q)
      || normalizeTr([r.brand, r.model].join(' ')).includes(q)
      || r.current_drivers.some((d) => normalizeTr(d.full_name || '').includes(q))));
  }, [data, filter, search]);

  const s = data?.summary;
  const openRow = (r) => {
    if (view === 'cards') { setEditFor(r); return; }
    setFilter('all'); setSearch(''); setExpanded(r.id);
    setTimeout(() => document.querySelector(`[data-testid="vehicle-overview-row-${r.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };
  const kpi = (key, icon, tone, label, value) => (
    <Kpi testId={`vehicle-kpi-${key}`} icon={icon} tone={tone} label={label} value={value ?? 0} active={filter === key} onClick={() => setFilter(filter === key ? 'all' : key)} />
  );
  const freshEdit = editFor && (data?.rows || []).find((r) => r.id === editFor.id);

  return (
    <div className="space-y-6" data-testid="vehicle-overview-page">
      <section className="relative overflow-hidden rounded-2xl bg-slate-900 p-5 sm:p-6 text-white">
        <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 translate-x-1/4 -translate-y-1/3 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300">Filo yönetimi</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-black flex items-center gap-2"><ClipboardList className="h-7 w-7 text-sky-400" />Araç Özeti & Atama</h1>
            <p className="text-sm text-slate-400 mt-1">Eksik bilgiler, uyarılar, şoför ve vergunning ataması tek ekranda</p>
          </div>
          {s && <p className="text-sm text-slate-300"><b className="text-white text-lg">{s.complete}</b> / {s.total} araç bilgileri tam</p>}
        </div>
        {s && (
          <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
            {kpi('all', Car, 'text-sky-400', 'Toplam araç', s.total)}
            {kpi('unassigned', UserX, 'text-slate-400', 'Boşta', s.unassigned)}
            {kpi('rental', KeyRound, 'text-sky-400', 'Kiralık', s.rental)}
            {kpi('maintenance', Wrench, 'text-violet-400', 'Bakımda', s.maintenance)}
            {kpi('alerts', AlertTriangle, 'text-red-400', 'Uyarısı olan', s.with_warnings)}
            {kpi('no_vergunning', FileBadge, 'text-indigo-400', 'Vergunning yok', s.no_vergunning)}
            {kpi('missing_info', AlertTriangle, 'text-amber-400', 'Eksik bilgi', s.missing_info)}
            {kpi('complete', CheckCircle2, 'text-emerald-400', '%100 tamam', s.complete)}
          </div>
        )}
      </section>

      {data && <VehicleAlerts rows={data.rows} onOpen={openRow} />}

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="vehicle-overview-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
        {isSuper && (
          <Select value={firmId || 'all'} onValueChange={(v) => setFirmId(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="vehicle-overview-firm-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm firmalar</SelectItem>
              {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <span className="text-xs text-slate-500" data-testid="vehicle-overview-count">{rows.length} araç</span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap sm:flex-nowrap items-center gap-2">
          <div className="relative w-full sm:w-auto">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Plaka, marka veya şoför ara…" className="h-9 pl-8 w-full sm:w-64" data-testid="vehicle-overview-search" />
          </div>
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5" role="group">
            {[['cards', LayoutGrid, 'Kart'], ['table', Rows3, 'Tablo']].map(([k, Icon, label]) => (
              <button key={k} type="button" onClick={() => setView(k)} data-testid={`vehicle-view-${k}`} title={label}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${view === k ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}>
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'cards' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="vehicle-card-grid">
          {rows.length === 0 && <p className="col-span-full py-10 text-center text-slate-400">Kayıt yok</p>}
          {rows.map((r) => <VehicleCard key={r.id} r={r} brandIcons={brandIcons} onAssign={setAssignFor} onVergunning={setVergFor} onEdit={setEditFor} />)}
        </div>
      ) : (
        <VehicleOverviewTable rows={rows} expanded={expanded} onToggle={(id) => setExpanded((e) => (e === id ? null : id))}
          onAssign={setAssignFor} onVergunning={setVergFor} renderEditor={(r) => <RowEditor key={r.id} row={r} onSaved={load} />} />
      )}

      {freshEdit && (
        <Dialog open onOpenChange={(o) => !o && setEditFor(null)}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="vehicle-quick-edit-dialog">
            <DialogHeader className="px-4 pt-4"><DialogTitle>Hızlı düzenle — {freshEdit.license_plate}</DialogTitle></DialogHeader>
            <RowEditor key={freshEdit.id} row={freshEdit} onSaved={load} />
          </DialogContent>
        </Dialog>
      )}
      {assignFor && <VehicleAssignDialog open vehicle={assignFor} onClose={() => setAssignFor(null)} onAssigned={() => { setAssignFor(null); load(); }} />}
      {vergFor && <VergunningDialog vehicle={vergFor} onClose={() => setVergFor(null)} onSaved={load} />}
    </div>
  );
}
